import React from "react";
// UI Framework
import { toast } from "react-toastify";
// Common Components
import {
  StaticMultiSelectDropdown,
  StaticDropdown,
  TextInput,
  Button,
} from "src/Components";
// Requests
import {
  getWalletUsers,
  getWallets,
  setTransfer,
  getUserWalletsInfo,
} from "src/Requests/imePortfolio";
// Utilities
import { packetErrorHandler } from "src/Utils/requestUtils";
import { addComma } from "src/helper";
// Types
import {
  ICreateWallet,
  IUserWalletsInfo,
  IDropdownItem,
} from "src/Interfaces/imePortfolio";

interface IProps {
  setRefreshFlag: (value: boolean) => void;
}

const SearchParamUI: React.FC<IProps> = ({ setRefreshFlag }) => {
  // states
  const [userWalletInfo, setUserWalletInfo] = React.useState(
    [] as IUserWalletsInfo[],
  );
  const [sourceBalance, setSourceBalance] = React.useState(
    [] as IUserWalletsInfo[],
  );
  const [destinationBalance, setDestinationBalance] = React.useState(
    [] as IUserWalletsInfo[],
  );
  const [users, setUsers] = React.useState([] as IDropdownItem[]);
  const [wallets, setWallets] = React.useState([] as IDropdownItem[]);
  const [formValues, setFormValues] = React.useState({} as ICreateWallet);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    setLoading(true);
    getWalletUsers().then((res) => setUsers(res));
    getWallets().then((res) => setWallets(res));
    getUserWalletsInfo()
      .then((res) => setUserWalletInfo(res))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    if (formValues.userIds && formValues.srcWallet) {
      const sourceInfo = userWalletInfo
        .filter((user) => formValues.userIds.includes(user.userId.toString()))
        .map((userInfo) => ({
          ...userInfo,
          userName: users.find((user) => user.id == userInfo.userId)?.name,
          wallets: userInfo.wallets.filter(
            (wallet) => wallet.walletType == formValues.srcWallet,
          ),
        }))
        .filter((user) => user.wallets.length > 0);
      setSourceBalance(sourceInfo);
    }
    if (formValues.userIds && formValues.destWallet) {
      const destInfo = userWalletInfo
        .filter((user) => formValues.userIds.includes(user.userId.toString()))
        .map((userInfo) => ({
          ...userInfo,
          userName: users.find((user) => user.id == userInfo.userId)?.name,
          wallets: userInfo.wallets.filter(
            (wallet) => wallet.walletType == formValues.destWallet,
          ),
        }))
        .filter((user) => user.wallets.length > 0);
      setDestinationBalance(destInfo);
    }
  }, [formValues, userWalletInfo]);

  const changeHandler = (name: string, value: any) => {
    setFormValues({ ...formValues, [name]: value });
  };

  const isDisabled =
    !formValues.userIds ||
    formValues.userIds.length == 0 ||
    !formValues.srcWallet ||
    !formValues.destWallet ||
    !formValues.amount;

  const clickHandler = () => {
    setLoading(true);
    setTransfer(formValues)
      .then((resp) => {
        setFormValues({} as ICreateWallet);
        setSourceBalance([]);
        setDestinationBalance([]);
        setRefreshFlag(true);
        const info = resp.map((userInfo) => ({
          ...userInfo,
          userName: users.find((user) => user.id == userInfo.userId)?.name,
        }));
        toast.info(
          <>
            {info.length > 0 && (
              <div className="max-h-40 overflow-auto p-1 rounded-xs space-y-1">
                {info.map((r) => (
                  <div
                    key={r.userId}
                    className={`text-xs leading-normal ${r.isSuccessful ? "text-lime-600" : "text-red-600"}`}
                  >
                    {r.userName} -{" "}
                    {r.isSuccessful ? "تراکنش موفق" : r.errorMessage}
                  </div>
                ))}
              </div>
            )}
          </>,
        );
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  return (
    <div className="rounded p-3 flex flex-col gap-3 !w-[300px] h-fit mt-6 shadow-[0px_2px_1px_-1px_rgba(0,0,0,0.2),0px_1px_1px_0px_rgba(0,0,0,0.14),0px_1px_3px_0px_rgba(0,0,0,0.12)]">
      <StaticMultiSelectDropdown
        label=" اکانت"
        name="userIds"
        items={users}
        extraLabel
        onChange={changeHandler}
        value={formValues.userIds}
        withTagBox
      />
      <StaticDropdown
        dropdown={{
          name: "srcWallet",
          placeholder: "کیف مبدا",
          extraLabel: true,
        }}
        options={wallets}
        onChange={changeHandler}
        value={formValues.srcWallet}
      />
      {sourceBalance.length > 0 && (
        <div className="max-h-40 overflow-auto bg-amber-100 p-1 rounded-xs space-y-1">
          <div className="text-xs">موجودی مبدا:</div>
          {sourceBalance.map((user) => (
            <div key={user.userId} className="text-xs">
              {user.userName} - {addComma(user.wallets[0]?.balance.toString())}{" "}
              ریال
            </div>
          ))}
        </div>
      )}
      <StaticDropdown
        dropdown={{
          name: "destWallet",
          placeholder: "کیف مقصد",
          extraLabel: true,
        }}
        options={wallets}
        onChange={changeHandler}
        value={formValues.destWallet}
      />
      {destinationBalance.length > 0 && (
        <div className="max-h-40 overflow-auto bg-amber-100 p-1 rounded-xs space-y-1">
          <div className="text-xs">موجودی مقصد:</div>
          {destinationBalance.map((user) => (
            <div key={user.userId} className="text-xs">
              {user.userName} - {addComma(user.wallets[0]?.balance.toString())}{" "}
              ریال
            </div>
          ))}
        </div>
      )}
      <TextInput
        name="amount"
        label="مبلغ جابه‌جایی (ریال)"
        extraLabel
        onChange={changeHandler}
        value={formValues?.amount?.toString() || ""}
        kind="price"
      />
      <Button
        onClick={clickHandler}
        text="ثبت"
        variant="contained"
        loading={loading}
        disabled={isDisabled || loading}
      />
    </div>
  );
};

export default SearchParamUI;
